import React, { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UserMenu from '../components/UserMenu';
import logo from '../assets/Logo_laboratorio_simple_verde-removebg-preview.png';
import axios from 'axios';
import {
  listarPacientes,
  crearPaciente,
  actualizarPaciente,
  PacienteRequest,
  PacienteResponse,
} from '../api/pacienteApi';
import { getObrasSociales, ObraSocialOption } from '../api/obraSocialApi';
import { ApiError } from '../types/auth.types';
import Button from '../components/Button';
import ErrorBanner from '../components/ErrorBanner';
import Modal from '../components/Modal';
import Select from '../components/Select';
import styles from './AdminPage.module.css';

type Mode = 'list' | 'create' | 'edit';

const EMPTY_FORM: PacienteRequest = {
  nombre: '',
  apellido: '',
  dni: '',
  email: '',
  particular: false,
  nroAfiliado: '',
  obraSocial: '',
};

export default function PacientesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pacientes, setPacientes] = useState<PacienteResponse[]>([]);
  const [obrasSociales, setObrasSociales] = useState<ObraSocialOption[]>([]);
  const [mode, setMode] = useState<Mode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PacienteRequest>(EMPTY_FORM);
  const [filter, setFilter] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listarPacientes(), getObrasSociales()])
      .then(([pacs, obras]) => {
        setPacientes(pacs);
        setObrasSociales(obras);
      })
      .finally(() => setLoadingData(false));
  }, []);

  useEffect(() => {
    if (!loadingData && searchParams.get('crear') === '1') {
      openCreate();
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('crear');
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingData]);

  function set(field: Partial<PacienteRequest>) {
    setForm((prev) => ({ ...prev, ...field }));
  }

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
    setMode('create');
  }

  function openEdit(p: PacienteResponse) {
    setEditingId(p.id);
    setForm({
      nombre: p.nombre,
      apellido: p.apellido,
      dni: p.dni,
      email: p.email ?? '',
      particular: p.particular,
      nroAfiliado: p.nroAfiliado ?? '',
      obraSocial: p.obraSocial ?? '',
    });
    setError(null);
    setMode('edit');
  }

  function cancel() {
    setMode('list');
    setError(null);
  }

  function handleParticular(checked: boolean) {
    set({ particular: checked, nroAfiliado: checked ? '' : form.nroAfiliado, obraSocial: checked ? '' : form.obraSocial });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.nombre.trim()) { setError('El nombre es obligatorio.'); return; }
    if (!form.apellido.trim()) { setError('El apellido es obligatorio.'); return; }
    if (!/^\d{7,8}$/.test(form.dni.trim())) { setError('El DNI debe tener 7 u 8 dígitos.'); return; }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('El email no es válido.');
      return;
    }
    if (!form.particular) {
      if (!form.obraSocial.trim()) { setError('La obra social es obligatoria para pacientes no particulares.'); return; }
      if (!form.nroAfiliado.trim()) { setError('El número de afiliado es obligatorio para pacientes no particulares.'); return; }
    }
    setError(null);
    setLoading(true);
    try {
      const payload: PacienteRequest = {
        ...form,
        nombre: form.nombre.trim(),
        apellido: form.apellido.trim(),
        dni: form.dni.trim(),
        email: form.email.trim(),
        obraSocial: form.particular ? '' : form.obraSocial.trim(),
        nroAfiliado: form.particular ? '' : form.nroAfiliado.trim(),
      };
      if (mode === 'create') {
        const nuevo = await crearPaciente(payload);
        setPacientes((prev) => [...prev, nuevo].sort((a, b) => a.apellido.localeCompare(b.apellido)));
      } else if (editingId !== null) {
        const actualizado = await actualizarPaciente(editingId, payload);
        setPacientes((prev) => prev.map((p) => (p.id === actualizado.id ? actualizado : p)));
      }
      setMode('list');
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setError(err.response?.data?.mensaje ?? 'Error al guardar.');
      } else {
        setError('Error al guardar.');
      }
    } finally {
      setLoading(false);
    }
  }

  const filteredPacientes = pacientes.filter((p) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (
      p.nombre.toLowerCase().includes(q) ||
      p.apellido.toLowerCase().includes(q) ||
      p.dni.includes(q)
    );
  });

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate('/dashboard')}>← Volver</button>
        <img src={logo} alt="Logo" className={styles.topbarLogo} />
        <p className={styles.topbarTitle}>Pacientes</p>
        <p className={styles.topbarBrand}>Laboratorio Sagrado Corazón</p>
        <UserMenu />
      </header>

      <main className={styles.content}>
        {loadingData ? (
          <div className={styles.spinnerWrapper}>
            <div className={styles.spinner} />
            <p className={styles.spinnerText}>Cargando pacientes...</p>
          </div>
        ) : (
        <>
        {mode === 'list' && (
          <div className={`${styles.listActions} ${styles.listActionsWithTitle}`}>
            <h2 className={styles.listTitle}>
              Total
              <span className={styles.titleBadge}>{pacientes.length}</span>
            </h2>
            <button className={styles.newBtnContent} onClick={openCreate}>+ Nuevo paciente</button>
          </div>
        )}

        {/* ── Formulario ── */}
        <Modal
          isOpen={mode !== 'list'}
          onClose={cancel}
          title={mode === 'create' ? 'Nuevo paciente' : 'Editar paciente'}
          width={560}
          disableClose={loading}
        >
          {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.formRow}>
              <div className={styles.field}>
                <label className={styles.label}>Nombre <span className={styles.required}>*</span></label>
                <input className={styles.input} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} autoFocus />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Apellido <span className={styles.required}>*</span></label>
                <input className={styles.input} value={form.apellido} onChange={(e) => set({ apellido: e.target.value })} />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.field}>
                <label className={styles.label}>DNI <span className={styles.required}>*</span></label>
                <input className={styles.input} value={form.dni} onChange={(e) => set({ dni: e.target.value })} placeholder="Ej: 30123456" />
              </div>
              <div className={`${styles.field} ${styles.fieldCheckbox}`}>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" checked={form.particular} onChange={(e) => handleParticular(e.target.checked)} />
                  Paciente particular
                </label>
              </div>
            </div>

            {!form.particular && (
              <div className={styles.formRow}>
                <div className={styles.field}>
                  <Select
                    id="obraSocial"
                    label="Obra social"
                    value={form.obraSocial}
                    onChange={(value) => set({ obraSocial: value })}
                    options={obrasSociales.map((o) => ({ value: o.nombre, label: o.nombre }))}
                    placeholder="Seleccioná una obra social"
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>N° de afiliado</label>
                  <input className={styles.input} value={form.nroAfiliado} onChange={(e) => set({ nroAfiliado: e.target.value })} />
                </div>
              </div>
            )}

            <div className={styles.formRow}>
              <div className={styles.field}>
                <label className={styles.label}>Email</label>
                <input
                  className={styles.input}
                  type="email"
                  value={form.email}
                  onChange={(e) => set({ email: e.target.value })}
                  placeholder="paciente@email.com"
                />
              </div>
            </div>

            <div className={styles.formActions}>
              <Button type="submit" loading={loading} loadingText="Guardando...">Guardar</Button>
              <button type="button" className={styles.cancelBtn} onClick={cancel} disabled={loading}>Cancelar</button>
            </div>
          </form>
        </Modal>

        {/* ── Lista ── */}
        {pacientes.length === 0 ? (
          <p className={styles.emptyMsg}>No hay pacientes registrados.</p>
        ) : (
          <div className={styles.listCard}>
            <div className={styles.listFilter}>
              <input
                className={styles.filterInput}
                type="text"
                placeholder="Filtrar por nombre, apellido o DNI..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            <div className={styles.tableBody}>
              <div className={styles.tableHeaderPacientes}>
                <span>Apellido</span>
                <span>Nombre</span>
                <span>DNI</span>
                <span>Particular</span>
                <span>Obra social</span>
                <span>N° afiliado</span>
                <span></span>
              </div>
              {filteredPacientes.length === 0 ? (
                <p className={styles.emptyMsg}>No se encontraron pacientes.</p>
              ) : filteredPacientes.map((p, idx) => (
                <div key={p.id} className={`${styles.tableRowPacientes} ${idx % 2 === 0 ? styles.listRowEven : ''}`}>
                  <span className={styles.cellDesc}>{p.apellido}</span>
                  <span className={styles.cellDesc}>{p.nombre}</span>
                  <span className={styles.cellSecondary}>{p.dni}</span>
                  <span>
                    {p.particular
                      ? <span className={styles.badgeYes}>Sí</span>
                      : <span className={styles.badgeNo}>No</span>}
                  </span>
                  <span className={`${styles.cellSecondary} ${styles.cellEllipsis}`} title={p.obraSocial ?? ''}>{p.obraSocial ?? '—'}</span>
                  <span className={styles.cellSecondary}>{p.nroAfiliado ?? '—'}</span>
                  <button className={styles.editBtn} onClick={() => openEdit(p)}>Editar</button>
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
